import React, { useState, useEffect } from 'react';
import { Star, X, ArrowLeft, Apple, SmartphoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import '../i18n';
import { supportedLanguages } from '../supportedLanguages';
import { Helmet } from 'react-helmet-async';

const DownloadPage = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [activePlatform, setActivePlatform] = useState('android');
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const langPrefix = lang ? `/${lang}` : '/fr';
  const baseUrl = 'https://bible4.me';
  const canonicalUrl = `${baseUrl}${langPrefix}/download`;

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    if (isIOSDevice) {
      setActivePlatform('iphone');
    } else if (isAndroidDevice) {
      setActivePlatform('android');
    }
  }, []);

  const AppBanner = () => {
    if (!isVisible) return null;

    return (
      <div className="bg-white shadow-lg z-50 w-full">
        <div className="flex items-center p-3 max-w-6xl mx-auto">
          <div className="w-12 h-12 bg-amber-200 rounded-xl flex items-center justify-center mr-3">
            <span className="text-amber-900 font-bold text-lg">B4M</span>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900">Bible4Me</h3>
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map((_, i) => (
                  <Star key={i} size={12} className="text-amber-400" />
                ))}
              </div>
              <span className="text-xs text-amber-800">{t('home.appBanner.free', 'Gratuit')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a 
              href="/download"
              className="bg-amber-100 text-amber-900 px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-amber-200 transition-colors"
            >
              <SmartphoneIcon size={16} />
              <span>{t('home.download', 'Télécharger')}</span>
            </a>
            <button
              onClick={() => setIsVisible(false)}
              className="p-1.5 text-amber-900 hover:bg-amber-100 rounded-full transition-colors"
              aria-label={t('common.close', 'Fermer') || 'Fermer'}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleBackToHome = () => {
    navigate(`${langPrefix}`);
  };

  const shouldShowBothTabs = !isIOS && !isAndroid;

  return (
    <>
      <Helmet>
        <title>{t('download.title')} — Bible4Me</title>
        <meta name="description" content={t('download.description')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="min-h-screen bg-amber-50">
        <AppBanner />

        {/* Back to Home */}
        <div className="bg-amber-900 text-white py-2">
          <div className="max-w-6xl mx-auto px-4">
            <button 
              onClick={handleBackToHome}
              className="flex items-center gap-1 text-sm hover:text-amber-200 transition-colors"
            >
              <ArrowLeft size={16} />
              <span>{t('common.back', "Retour à l'accueil")}</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="py-12 px-4">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden">
            {/* Header */}
            <div className="bg-amber-200 p-5">
              <div className="flex items-center gap-3 mb-2">
                <SmartphoneIcon className="text-amber-900" size={24} />
                <h1 className="text-2xl font-bold text-amber-900">{t('download.title', 'Télécharger Bible4Me')}</h1>
              </div>
              <p className="text-amber-800">
                {t('download.description', 'Téléchargez gratuitement Bible4Me sur votre appareil mobile.')}
              </p>
            </div>

            {/* Platform Tabs - only show if we should display both */}
            {shouldShowBothTabs && (
              <div className="flex border-b border-amber-200">
                <button 
                  className={`flex items-center gap-2 flex-1 py-3 px-4 font-medium ${activePlatform === 'android' ? 'bg-amber-100 text-amber-900' : 'text-amber-700 hover:bg-amber-50'}`}
                  onClick={() => setActivePlatform('android')}
                >
                  <SmartphoneIcon size={18} />
                  <span>{t('download.android', 'Android')}</span>
                </button>
                <button 
                  className={`flex items-center gap-2 flex-1 py-3 px-4 font-medium ${activePlatform === 'iphone' ? 'bg-amber-100 text-amber-900' : 'text-amber-700 hover:bg-amber-50'}`}
                  onClick={() => setActivePlatform('iphone')}
                >
                  <Apple size={18} />
                  <span>{t('download.iphone', 'iPhone')}</span>
                </button>
              </div>
            )}

            {/* Content */}
            <div className="p-5">
              {(activePlatform === 'android' || isAndroid) && (
                <>
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-amber-900 mb-2">{t('download.androidSection.title', 'Pour Android')}</h2>
                    <p className="text-amber-800 mb-4">
                      {t('download.androidSection.description', 'Téléchargez Bible4Me depuis Google Play Store.')}
                    </p>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-6">
                      <div className="flex items-center gap-2 text-amber-900 font-medium mb-2">
                        <SmartphoneIcon size={16} />
                        <span>{t('download.androidSection.requirements.title', 'Prérequis')}</span>
                      </div>
                      <ul className="list-disc list-inside text-amber-800 text-sm space-y-1">
                        <li>{t('download.androidSection.requirements.device', 'Un appareil Android avec version 8.0 ou plus récente')}</li>
                        <li>{t('download.androidSection.requirements.internet', 'Connexion internet pour la synchronisation')}</li>
                      </ul>
                    </div>
                  </div>
                  <div className="text-center mb-6">
                    <a 
                      href="https://play.google.com/store/apps/details?id=com.bible4md.app" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg inline-block transition-colors"
                    >
                      {t('download.androidSection.downloadButton', 'Télécharger sur Google Play')}
                    </a>
                    <p className="mt-4 text-sm text-amber-700">
                      {t('download.androidSection.redirectNote', 'Vous serez redirigé vers Google Play Store pour installer Bible4Me.')}
                    </p>
                  </div>
                </>
              )}

              {(activePlatform === 'iphone' || isIOS) && (
                <>
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-amber-900 mb-2">{t('download.iosSection.title', 'Pour iPhone')}</h2>
                    <p className="text-amber-800 mb-4">
                      {t('download.iosSection.description', 'Téléchargez Bible4Me depuis l’App Store.')}
                    </p>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-6">
                      <div className="flex items-center gap-2 text-amber-900 font-medium mb-2">
                        <Apple size={16} />
                        <span>{t('download.iosSection.requirements.title', 'Prérequis')}</span>
                      </div>
                      <ul className="list-disc list-inside text-amber-800 text-sm space-y-1">
                        <li>{t('download.iosSection.requirements.device', 'Un iPhone ou iPad avec iOS 14.0 ou plus récent')}</li>
                        <li>{t('download.iosSection.requirements.internet', 'Connexion internet pour la synchronisation')}</li>
                      </ul>
                    </div>
                  </div>
                  <div className="text-center mb-6">
                    <a 
                      href="https://apps.apple.com/fr/app/bible4me/id6743235720" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg inline-block transition-colors"
                    >
                      {t('download.iosSection.downloadButton', 'Télécharger sur l’App Store')}
                    </a>
                    <p className="mt-4 text-sm text-amber-700">
                      {t('download.iosSection.redirectNote', 'Vous serez redirigé vers l’App Store pour installer Bible4Me.')}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="max-w-md mx-auto mt-6 text-center text-sm text-amber-700">
            <p>{t('download.thankYou', 'Merci de télécharger Bible4Me !')}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadPage;